import { ChangeEvent } from "react";
import { useSearchParams } from "react-router-dom";

/* hooks */
import { useNavigate } from "react-router-dom";
import { useParceiroSave } from "./hooks/useParceiroSave";

import Alert from '@mui/material/Alert';
import {
  Typography,
  CircularProgress,
  Button,
  TextField,
  Card,
  CardContent,
} from "@mui/material";

import CardLayout from "components/atoms/CardLayout/CardLayout";
import SearchSelect from "components/atoms/SearchSelect/SearchSelect";
import CpfField from "../../atoms/CpfField/CpfField";
import CnpjField from "../../atoms/CnpjField/CnpjField";
import EmailAutocomplete from "../../atoms/EmailAutocomplete/EmailAutoComplete";
import TelefoneField from "../../atoms/TelefoneField/TelefoneField";

/* Styles */
import styles from './GestaoParceiros.module.css';
import styles_filter from "../../../styles/Filter.module.css";
import styles_input from "../../../styles/Input.module.css";
import styles_table from "../../../styles/Table.module.css";
import styles_button from "../../../styles/Button.module.css";
import styles_general from "../../../styles/General.module.css";

const NewParceiro = () => {

  const [searchParams] = useSearchParams();
  const partnerId = searchParams.get("id");

  const navigate = useNavigate();

  const { 
    msgSuccess,
    msgError,
    errors,
    loading,
    loadingSave,
    tiposInstituicao,
    parceiro,
    handleRespName,
    handleInputChange,
    handleChange,
    handleEmailChange,
    saveParceiro,
  } = useParceiroSave(partnerId);

  if (loading) {
    return (
      <div className={styles["loading-container"]}>
        <CircularProgress />
        <Typography>Carregando...</Typography>
      </div>
    );
  }

  return (
    <Card className={styles.card}>
      <CardContent>
        <div className={styles.title}>
          Cadastrar Instituição
        </div>
        {msgSuccess && (
          <div className={styles_table.message}>
            <Alert severity="success">
              {msgSuccess}
            </Alert>
          </div>
        )}
        {msgError && (
          <div className={styles_table.message}>
            <Alert severity="error">
              {msgError}
            </Alert>
          </div>
        )}
        
        <div className={styles.marginTopMd}>
          <CardLayout title="Dados da Instituição">
            <div className={`${styles_filter.grid12} ${styles_general.p4}`}>
              <div className={styles_filter.inputGroup6}>
                <label className={styles_input.titleInput}>
                  Nome da Instituição *
                </label>
                <TextField
                  size="small"
                  placeholder=""
                  variant="standard"
                  autoFocus={false}
                  fullWidth
                  data-testid="nome"
                  value={parceiro.nome}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => 
                    handleInputChange(e.target.value, 'nome')
                  }
                  required
                  inputProps={{ maxLength: 150 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    className: styles_input.input,
                  }}
                  sx={{
                    '& .MuiInput-underline:before': { borderBottom: 'none' },
                    '& .MuiInput-underline:after': { borderBottom: 'none' },
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                  }}
                  error={!!errors.nome}
                  helperText={errors.nome}
                />
              </div>

              <div className={styles_filter.inputGroup6}>
                <label className={styles_input.titleInput}>
                  CNPJ *
                </label>
                <CnpjField
                  size="small"
                  placeholder=""
                  variant="standard"
                  autoFocus={false}
                  fullWidth
                  value={parceiro.cnpj}
                  onUpdate={(value) => handleInputChange(value, 'cnpj')}
                  required
                  customClassName={styles_input.input}
                  error={!!errors.cnpj}
                  helperText={errors.cnpj}
                />
              </div>

              <div className={styles_filter.inputGroup6}>
                <label className={styles_input.titleInput}>
                  E-mail institucional *
                </label>
                <EmailAutocomplete
                  label=""
                  value={parceiro.email}
                  onChange={(value) => handleEmailChange(value, 'email')}
                  customClassName={styles_input.input}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </div>

              <div className={styles_filter.inputGroup6}>
                <label className={styles_input.titleInput}>
                  Telefone Institucional *
                </label>
                <TelefoneField
                  label=""
                  customClass={styles_input.input}
                  fullWidth
                  value={parceiro.telefone || ""}
                  onChange={(value) => handleInputChange(value, 'telefone')}
                  error={!!errors.telefone}
                  helperText={errors.telefone}
                />
              </div>

              <div className={styles_filter.inputGroup6}>
                <label className={styles_input.titleInput}>
                  Tipo de Instituição *
                </label>
                <SearchSelect
                  label=""
                  key="tipo_instituicao"
                  options={tiposInstituicao}
                  value={parceiro.tipo_id}
                  onChange={(id) => handleChange("tipo_id", id)}
                  error={!!errors.tipo_id}
                  helperText={errors.tipo_id}
                  paperClassname={styles_input.paper}
                  popperClassname={styles_input.popper}
                  labelClassname={styles_input.labelSelect}
                  inputClassname={styles_input.inputSelect}
                />
              </div>
            </div>
          </CardLayout>
        </div>
        
        <div className={styles.marginTopMd}>
          <CardLayout title="Dados da Responsável Legal">
            <div className={`${styles_general.p4}`}>
              <div className={styles_filter.grid12}>
                <div className={styles_filter.inputGroup6}>
                  <label className={styles_input.titleInput}>
                    Nome do Responsável Legal *
                  </label>
                  <TextField
                    size="small"
                    placeholder=""
                    variant="standard"
                    autoFocus={false}
                    fullWidth
                    data-testid="nome"
                    value={parceiro.resp_nome}
                    onChange={handleRespName}
                    required
                    inputProps={{ maxLength: 150 }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      className: styles_input.input,
                    }}
                    sx={{
                      '& .MuiInput-underline:before': { borderBottom: 'none' },
                      '& .MuiInput-underline:after': { borderBottom: 'none' },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                    }}
                    error={!!errors.resp_nome}
                    helperText={errors.resp_nome}
                  />
                </div>
                <div className={styles_filter.inputGroup6}>
                  <label className={styles_input.titleInput}>
                    CPF *
                  </label>
                  <CpfField
                    size="small"
                    placeholder=""
                    variant="standard"
                    autoFocus={false}
                    value={parceiro.resp_cpf}
                    onUpdate={(value) => handleInputChange(value, 'resp_cpf')}
                    customClassName={styles_input.input}
                    error={!!errors.resp_cpf}
                    helperText={errors.resp_cpf}
                  />
                </div>
                <div className={styles_filter.inputGroup6}>
                  <label className={styles_input.titleInput}>
                    E-mail*
                  </label>
                  <EmailAutocomplete
                    label=""
                    value={parceiro.resp_email}
                    onChange={(value) => handleEmailChange(value, 'resp_email')}
                    customClassName={styles_input.input}
                    error={!!errors.resp_email}
                    helperText={errors.resp_email}
                  />
                </div>
                <div className={styles_filter.inputGroup6}>
                  <label className={styles_input.titleInput}>
                    Celular
                  </label>
                  <TelefoneField
                    label=""
                    customClass={styles_input.input}
                    fullWidth
                    value={parceiro.resp_celular || ""}
                    onChange={(value) => handleInputChange(value, 'resp_celular')}
                    error={!!errors.resp_celular}
                    helperText={errors.resp_celular}
                  />
                </div>
              </div>
            </div>
          </CardLayout>
        </div>

        <div className={styles.marginTopMd}>
          <div className={styles_filter.lineButton}>
            {/* Limpar */}
            <Button 
              variant="contained"
              onClick={() => navigate(`/partners`)} 
              className={styles_filter.buttonClean}
              color="primary"
            >
              Voltar
            </Button>
            {/* Pesquisar */}
            <Button
              variant="contained"
              onClick={saveParceiro}
              color="primary"
              disabled={loadingSave} 
              className={`${styles_filter.buttonSearch} ${(loadingSave) ? styles_button.buttonLoad : ''} `}
            >
              {loadingSave ? (
                <>
                <div className={styles_button.spinner}></div>
                <span>Salvando...</span>
                </>
              ) : (
              <span>Salvar</span>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NewParceiro;
